import { motion } from "framer-motion";
import { MapPin, Sparkles, Bike, Wrench, History, Users } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const ICONS = { f1: MapPin, f2: Sparkles, f3: Bike, f4: Wrench, f5: History, f6: Users };
const KEYS = ["f1", "f2", "f3", "f4", "f5", "f6"];

const Card = ({ k, index, t }) => {
  const Icon = ICONS[k];
  const data = t.features[k];
  return (
    <motion.div
      initial={{ y: 22, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease, delay: index * 0.06 }}
      data-testid="feature-card"
      className="group relative overflow-hidden rounded-[20px] border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-7 shadow-[0_18px_60px_rgba(0,0,0,0.45)] transition-colors duration-300 hover:bg-white/[0.06] hover:border-white/16 hover:shadow-[0_0_0_1px_rgba(57,255,20,0.22),0_18px_60px_rgba(0,0,0,0.45)]"
    >
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      <span className="pointer-events-none absolute -top-20 -left-20 w-56 h-56 rounded-full bg-[#39FF14]/8 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14] shadow-[0_0_24px_rgba(57,255,20,0.15)]">
          <Icon className="w-5 h-5" />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">{data.badge}</span>
      </div>
      <h3 className="font-display font-semibold text-xl text-white mt-5">{data.title}</h3>
      <p className="mt-2 text-sm text-white/65 leading-relaxed">{data.desc}</p>
    </motion.div>
  );
};

export const Features = () => {
  const { t } = useLanguage();
  return (
    <section id="features" data-testid="features-section" className="relative py-20 sm:py-28 lg:py-32 bg-[#07090B]">
      <div className="absolute inset-x-0 top-0 h-40 section-edge-glow pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 14, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="max-w-2xl"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#39FF14]/90">{t.features.eyebrow}</span>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight">
            {t.features.title1}<br />{t.features.title2}
          </h2>
          <p className="mt-4 text-white/65 text-base sm:text-lg leading-relaxed">{t.features.subhead}</p>
        </motion.div>
        <div data-testid="features-grid" className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {KEYS.map((k, i) => <Card key={k} k={k} index={i} t={t} />)}
        </div>
      </div>
    </section>
  );
};
